/**
 * faqData.js
 *
 * Provides game-aware FAQ data for all 8 supported game types:
 * - classic5050
 * - chaseTheAce
 * - progressive5050
 * - modified5050
 * - eliminationDraw
 * - prizeDraw
 * - squareGame
 * - superballBingo
 */

import { DEFAULT_TARGET_CARD, formatCtaTargetCardText } from './ctaHelpers';

export function getGameFaqs(
  gameType = 'classic5050',
  gameName = '50/50',
  t = (k, fallback) => fallback,
  targetCard = DEFAULT_TARGET_CARD,
  language = 'en',
  isGuestCheckout = false
) {
  const resolvedGameName = gameName || '50/50';

  // Base Licensing section
  const licensingSection = {
    category: t('faq.licensingCategory', 'Licensing & Organization'),
    questions: [
      {
        question: t('faq.licensingQ1', 'Who is operating this fundraiser?'),
        answer: t('faq.licensingA1', 'This fundraiser is operated by TLF Fundraising Solutions in partnership with licensed charitable organizations. All proceeds benefit local community causes and registered charities.'),
      },
      {
        question: t('faq.licensingQ2', 'Is this fundraiser licensed?'),
        answer: t('faq.licensingA2', 'Yes, our fundraising operations are fully licensed and compliant with all provincial gaming regulations. All draws are conducted under proper licensing authorities.'),
      },
    ],
  };

  // Base General Questions
  const generalSection = {
    category: t('faq.generalCategory', 'General Questions'),
    questions: [
      {
        question: isGuestCheckout
          ? t('faq.generalQ1_guest', 'Do I have to register or create an account to play?')
          : t('faq.generalQ1', 'Do I have to register or log in to play?'),
        answer: isGuestCheckout
          ? t('faq.generalA1_guest', 'No, account registration is not required. You can complete your purchase as a guest by simply entering your contact and billing details at checkout. If you\'d like, you can also create an account to easily track your tickets and order history.')
          : t('faq.generalA1', `Yes, creating an account or providing your contact details allows you to participate in our ${resolvedGameName} draws, ensures compliance with gaming regulations, and enables us to notify you immediately if you win.`),
      },
      {
        question: t('faq.generalQ2', 'How do I purchase tickets?'),
        answer: t('faq.generalA2', 'Select the ticket package you want, choose any required options (such as a card, square, or ball if applicable), and complete payment securely through our checkout system.'),
      },
      {
        question: t('faq.generalQ3', 'When do draws take place?'),
        answer: t('faq.generalA3', 'Draw dates and times are displayed on the main page with a live countdown. All participants receive email notifications with official draw results.'),
      },
      {
        question: t('faq.generalQ4', 'How will I know if I win?'),
        answer: t('faq.generalA4', 'Winners are notified immediately by email and phone. Winner names and numbers are also published on our official Winners page after each draw.'),
      },
      {
        question: t('faq.generalQ5', 'What payment methods do you accept?'),
        answer: t('faq.generalA5', 'We accept major credit cards (Visa, Mastercard, American Express), Visa Debit, and Debit Mastercard through our secure payment processing system. Please note that standard bank-only Interac debit cards without a Visa or Mastercard logo cannot be used for online checkout.'),
      },
    ],
  };

  // Game-Specific Section ("About [Game Name]")
  let gameSpecificSection = {
    category: `About ${resolvedGameName}`,
    questions: [],
  };

  switch (gameType) {
    case 'chaseTheAce':
      gameSpecificSection = {
        category: t('faq.chaseTheAceCategory', 'About Chase the Ace'),
        questions: [
          {
            question: t('faq.ctaQ1', 'How does Chase the Ace work?'),
            answer: t('faq.ctaA1', 'In Chase the Ace, a winning ticket is drawn each week for a weekly prize pot. The weekly winner also reveals a card from the deck. If the Target Card ({{targetCard}}) is revealed, they win the Progressive Jackpot! If not, the card is removed from the deck and the jackpot rolls over to the next draw.'),
          },
          {
            question: t('faq.ctaQ2', 'What happens if the {{targetCard}} is not drawn?'),
            answer: t('faq.ctaA2', 'The revealed card is permanently removed from the deck, which improves your odds for the next draw. The progressive jackpot rolls over and continues to grow until the {{targetCard}} is caught!'),
          },
          {
            question: t('faq.ctaQ3', 'How do envelope and card selections work?'),
            answer: t('faq.ctaA3', 'Depending on the draw setup, you either pick your preferred envelope number online during checkout or, if set to post-draw selection, the winning ticket holder selects an envelope after their ticket is drawn or in person at the draw event.'),
          },
          {
            question: t('faq.ctaQ4', 'How do my odds change each week?'),
            answer: t('faq.ctaA4', 'Your odds of winning the weekly ticket draw depend on total tickets sold. Your odds of finding the {{targetCard}} improve every week as cards are eliminated (e.g. 1 in 52, 1 in 51, 1 in 50, etc.).'),
          },
        ],
      };
      break;

    case 'progressive5050':
      gameSpecificSection = {
        category: t('faq.progressiveCategory', 'About Progressive 50/50'),
        questions: [
          {
            question: t('faq.progQ1', 'How does Progressive 50/50 work?'),
            answer: t('faq.progA1', 'Progressive 50/50 builds a jackpot over time. A portion of ticket sales goes to the weekly prize, and a portion builds the progressive jackpot. If the grand prize is not awarded on a draw, the progressive jackpot rolls over to the next draw!'),
          },
          {
            question: t('faq.progQ2', 'What is a rollover?'),
            answer: t('faq.progA2', 'A rollover occurs when a progressive jackpot isn\'t hit or claimed. The accumulated jackpot carries over to the next draw, increasing the total prize amount for the next winner.'),
          },
          {
            question: t('faq.progQ3', 'What are my odds of winning?'),
            answer: t('faq.progA3', 'Your odds of winning the draw depend on the total number of tickets purchased for that specific draw.'),
          },
        ],
      };
      break;

    case 'modified5050':
      gameSpecificSection = {
        category: t('faq.modifiedCategory', 'About Modified 50/50'),
        questions: [
          {
            question: t('faq.modQ1', 'How does Modified 50/50 work?'),
            answer: t('faq.modA1', 'Modified 50/50 offers structured ticket packages across multi-week or series draw rounds. You can purchase tickets for single or multiple draws ahead of time, with 50% of total sales going to the prize pot and 50% supporting community programs.'),
          },
          {
            question: t('faq.modQ2', 'Can I purchase tickets for future weeks in advance?'),
            answer: t('faq.modA2', 'Yes! Modified 50/50 allows you to select the number of draws or weeks you wish to participate in during a single checkout.'),
          },
        ],
      };
      break;

    case 'eliminationDraw':
      gameSpecificSection = {
        category: t('faq.eliminationCategory', 'About Elimination Draw'),
        questions: [
          {
            question: t('faq.elimQ1', 'How does an Elimination Draw work?'),
            answer: t('faq.elimA1', 'In an Elimination Draw, tickets are eliminated one by one in reverse draw order. Instead of the first ticket winning, the last ticket remaining in the draw wins the grand jackpot!'),
          },
          {
            question: t('faq.elimQ2', 'Are there secondary or milestone prizes?'),
            answer: t('faq.elimA2', 'Yes! Elimination Draws feature milestone cash or physical prizes awarded at specific elimination numbers (e.g. 1st ticket drawn, 50th ticket drawn, or 100th ticket drawn).'),
          },
          {
            question: t('faq.elimQ3', 'Can remaining participants split the prize?'),
            answer: t('faq.elimA3', 'When only a few tickets remain near the final rounds, rules may allow remaining ticket holders to agree to split the grand prize, subject to official draw regulations.'),
          },
        ],
      };
      break;

    case 'prizeDraw':
      gameSpecificSection = {
        category: t('faq.prizeDrawCategory', 'About Prize Draw'),
        questions: [
          {
            question: t('faq.prizeQ1', 'How does a Prize Draw work?'),
            answer: t('faq.prizeA1', 'Instead of a cash 50/50 pot, a Prize Draw features specific physical or experiential prizes such as vehicles, vacations, or electronics. Each ticket purchased gives you entries into the prize draw.'),
          },
          {
            question: t('faq.prizeQ2', 'Can I win multiple prizes with one purchase?'),
            answer: t('faq.prizeA2', 'Depending on the draw rules, your ticket entries may be eligible for multiple bonus or early-bird prize draws in addition to the grand prize draw.'),
          },
          {
            question: t('faq.prizeQ3', 'Is there a cash alternative for physical prizes?'),
            answer: t('faq.prizeA3', 'Some prize draws offer a cash option in lieu of the physical prize. Check the specific draw details on the homepage or official rules page.'),
          },
        ],
      };
      break;

    case 'squareGame':
      gameSpecificSection = {
        category: t('faq.squareGameCategory', 'About Square Game'),
        questions: [
          {
            question: t('faq.squareQ1', 'How does the Square Game work?'),
            answer: t('faq.squareA1', 'Players purchase numbered squares on a 100-square or 1,000-square grid. Once the board fills or sales close, winning squares are selected at random or determined by official event scores.'),
          },
          {
            question: t('faq.squareQ2', 'How are winning squares determined?'),
            answer: t('faq.squareA2', 'Depending on the game setup, winning squares are either drawn randomly by certified RNG or matched to digits of live sporting event scores (e.g. end of quarter or final scores).'),
          },
          {
            question: t('faq.squareQ3', 'Can I buy multiple squares on the grid?'),
            answer: t('faq.squareA3', 'Yes, you can select as many available squares as you like on the grid during checkout.'),
          },
        ],
      };
      break;

    case 'superballBingo':
      gameSpecificSection = {
        category: t('faq.superballCategory', 'About Superball Bingo'),
        questions: [
          {
            question: t('faq.superQ1', 'How does Superball Bingo work?'),
            answer: t('faq.superA1', 'Players purchase bingo cards and choose a ball number from 1 to 75. In each session, one ticket is drawn for the session prize (25% of sales). If the winner\'s chosen ball matches the secret golden Superball, they ALSO win the Progressive Jackpot!'),
          },
          {
            question: t('faq.superQ2', 'What if my chosen ball is not the Superball?'),
            answer: t('faq.superA2', 'You still win! The session winner always receives the session prize. The Superball is a bonus jackpot trigger that rolls over if not hit.'),
          },
          {
            question: t('faq.superQ3', 'How often do sessions run?'),
            answer: t('faq.superA3', 'Superball Bingo sessions run on regular schedules. Each session has a new randomly assigned Superball position.'),
          },
        ],
      };
      break;

    case 'classic5050':
    default:
      gameSpecificSection = {
        category: t('faq.classicCategory', 'About Classic 50/50'),
        questions: [
          {
            question: t('faq.classicQ1', 'How does a 50/50 draw work?'),
            answer: t('faq.classicA1', 'In a Classic 50/50 draw, 50% of the total ticket sales go to the winner as a cash prize, and 50% directly supports the charitable cause. The more tickets sold, the bigger the jackpot grows!'),
          },
          {
            question: t('faq.classicQ2', 'What are my odds of winning?'),
            answer: t('faq.classicA2', 'Your odds of winning depend on the total number of tickets sold for the draw and how many tickets you purchase.'),
          },
          {
            question: t('faq.classicQ3', 'Can I buy tickets for someone else?'),
            answer: t('faq.classicA3', 'Tickets must be purchased under your own name and account. However, you can gift the winnings to anyone you choose if you win.'),
          },
        ],
      };
      break;
  }

  // Prizes & Winnings Section
  const prizesSection = {
    category: t('faq.prizesCategory', 'Prizes & Winnings'),
    questions: [
      {
        question: t('faq.prizesQ1', 'How do I claim my prize?'),
        answer: t('faq.prizesA1', 'If you win, the charitable organization will contact you directly by phone and email to verify your identity and eligibility in accordance with lottery licensing regulations. Once verified, official arrangements will be made to issue your payout via Electronic Funds Transfer, direct deposit, or cheque.'),
      },
      {
        question: t('faq.prizesQ2', 'Are there taxes on winnings?'),
        answer: t('faq.prizesA2', 'In Canada, lottery, raffle, and 50/50 winnings are 100% tax-free! Winners do not pay any income tax on cash prizes or physical winnings.'),
      },
      {
        question: t('faq.prizesQ3', 'Is there a minimum jackpot?'),
        answer: t('faq.prizesA3', 'Each draw may have a minimum guaranteed jackpot. Check the current draw details on the homepage for specific information.'),
      },
    ],
  };

  // Format target card placeholders in Chase the Ace FAQs
  if (gameType === 'chaseTheAce') {
    gameSpecificSection.questions = gameSpecificSection.questions.map((q) => ({
      question: formatCtaTargetCardText(q.question, targetCard, '', language),
      answer: formatCtaTargetCardText(q.answer, targetCard, '', language),
    }));
  }

  return {
    tagline: `${resolvedGameName.toUpperCase()} FAQ`,
    subtitle: `Find answers to common questions about our ${resolvedGameName} games and draws.`,
    sections: [
      licensingSection,
      generalSection,
      gameSpecificSection,
      prizesSection,
    ],
  };
}
